// Hospital-specific React Hooks
// Provides easy-to-use hooks for hospital-related API calls
import { useApi, useApiState, usePagination, useApiMutation } from './useApi.js';
import { hospitalService } from '../services';

// Hospital Profile Hooks
export const useHospitalProfile = (enabled = true) => {
  // Only fetch when enabled (e.g. skip on fleet dashboard so hospital endpoint is not hit)
  return useApi(hospitalService.getMyProfile.bind(hospitalService), [enabled]);
};

export const useUpdateHospitalProfile = () => {
  return useApiMutation();
};

export const useHospitalProfileById = (hospitalId) => {
  return useApi(
    hospitalService.getProfileById.bind(hospitalService, hospitalId),
    [hospitalId]
  );
};

// Hospital Dashboard Hooks
export const useHospitalDashboard = (params = {}) => {
  return useApi(
    hospitalService.getMyDashboard.bind(hospitalService, params),
    [params]
  );
};

export const useHospitalDashboardById = (hospitalId, params = {}) => {
  return useApi(
    hospitalService.getDashboardById.bind(hospitalService, hospitalId, params),
    [hospitalId, params]
  );
};

// Hospital Analytics Hooks
export const useHospitalAnalytics = (params = {}) => {
  return useApi(
    hospitalService.getAnalytics.bind(hospitalService, params),
    [params]
  );
};

export const useHospitalPerformanceComparison = (params = {}) => {
  return useApi(
    hospitalService.getPerformanceComparison.bind(hospitalService, params),
    [params]
  );
};

// Hospital Payments Hooks
export const useHospitalSettlements = (params = {}) => {
  return useApi(
    hospitalService.getSettlements.bind(hospitalService, params),
    [params]
  );
};

export const useProcessHospitalPayment = () => {
  return useApiMutation();
};

export const useHospitalPaymentSummary = (params = {}) => {
  return useApi(
    hospitalService.getPaymentSummary.bind(hospitalService, params),
    [params]
  );
};

// Hospital Groups Hooks
export const useHospitalGroups = (params = {}) => {
  return usePagination(hospitalService.getAllGroups.bind(hospitalService), params);
};

export const useCreateHospitalGroup = () => {
  return useApiMutation();
};

export const useHospitalGroupById = (groupId) => {
  return useApi(
    hospitalService.getGroupById.bind(hospitalService, groupId),
    [groupId]
  );
};

export const useUpdateHospitalGroup = () => {
  return useApiMutation();
};

export const useDeleteHospitalGroup = () => {
  return useApiMutation();
};

export const useAddGroupMember = () => {
  return useApiMutation();
};

export const useRemoveGroupMember = () => {
  return useApiMutation();
};

export const useGroupAnalytics = (groupId, params = {}) => {
  return useApi(
    hospitalService.getGroupAnalytics.bind(hospitalService, groupId, params),
    [groupId, params]
  );
};

// Hospital Notifications Hooks
export const useHospitalNotifications = (params = {}) => {
  return usePagination(hospitalService.getNotifications.bind(hospitalService), params);
};

export const useMarkNotificationAsRead = () => {
  return useApiMutation();
};

export const useMarkAllNotificationsAsRead = () => {
  return useApiMutation();
};

export const useSendEmergencyNotification = () => {
  return useApiMutation();
};

export const useNotificationStats = (params = {}) => {
  return useApi(
    hospitalService.getNotificationStats.bind(hospitalService, params),
    [params]
  );
};

// Hospital Bookings Hooks
export const useHospitalBookings = (params = {}) => {
  return usePagination(hospitalService.getMyBookings.bind(hospitalService), params);
};

export const useHospitalBookingById = (bookingId) => {
  return useApi(
    hospitalService.getBookingDetails.bind(hospitalService, bookingId),
    [bookingId]
  );
};

export const useUpdateBookingStatus = () => {
  return useApiMutation();
};

export const useBookingAnalytics = (params = {}) => {
  return useApi(
    hospitalService.getBookingAnalytics.bind(hospitalService, params),
    [params]
  );
};

// Hospital Reports Hooks
export const useGenerateReport = () => {
  return useApiMutation();
};

export const useReportHistory = (params = {}) => {
  return usePagination(hospitalService.getReportHistory.bind(hospitalService), params);
};

export const useDownloadReport = () => {
  return useApiMutation();
};

// Hospital Settings Hooks
export const useHospitalSettings = () => {
  return useApi(hospitalService.getSettings.bind(hospitalService));
};

export const useUpdateHospitalSettings = () => {
  return useApiMutation();
};

export const useEmergencyContacts = () => {
  return useApi(hospitalService.getEmergencyContacts.bind(hospitalService));
};

export const useUpdateEmergencyContacts = () => {
  return useApiMutation();
};

// Combined Hospital State Hook
export const useHospitalState = () => {
  const profile = useHospitalProfile();
  const dashboard = useHospitalDashboard();
  const analytics = useHospitalAnalytics();
  const notifications = useHospitalNotifications();
  const settings = useHospitalSettings();

  return {
    profile,
    dashboard,
    analytics,
    notifications,
    settings
  };
};
